<template>
  <div>
    <v-sheet elevation="1">
      <v-toolbar dense color="white">
        <v-toolbar-title>{{ screenTitle }}</v-toolbar-title>
      </v-toolbar>
      <v-container fluid>
        <error-message :error-object="errorObject" />
        <success-message :success-object="successObject" />
        <p style="margin: 5px 0 15px 0">
          安定ヨウ素剤の配布状況（発災時）は下の表を参照してください。条件を指定することで一覧から絞り込むことができます。
        </p>
        <!-- 検索部 -->
        <v-row>
          <v-col cols="6" md="3">
            <!-- 災害名称 -->
            <v-select
              v-model="searchItem.disaster"
              :items="disasters"
              label="災害名称"
              :placeholder="config.PLACEHOLDER_SELECT"
              item-text="disaster_name"
              item-value="disaster_no"
              return-object
              clearable
              outlined
            ></v-select>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="6" md="3">
            <!-- 配布日（開始） -->
            <v-text-field
              v-model="distDateFrom"
              :placeholder="config.PLACEHOLDER_SELECT_DATE"
              label="配布日（開始）"
              outlined
              clearable
              readonly
              @click:clear="searchItem.selectedDistDateFrom = null"
            >
              <template v-slot:append>
                <date-picker
                  v-model="searchItem.selectedDistDateFrom"
                  :max="searchItem.selectedDistDateTo"
                />
              </template>
            </v-text-field>
          </v-col>
          <v-col cols="6" md="3">
            <!-- 配布日（終了） -->
            <v-text-field
              v-model="distDateTo"
              :placeholder="config.PLACEHOLDER_SELECT_DATE"
              label="配布日（終了）"
              outlined
              clearable
              readonly
              @click:clear="searchItem.selectedDistDateTo = null"
            >
              <template v-slot:append>
                <date-picker
                  v-model="searchItem.selectedDistDateTo"
                  :min="searchItem.selectedDistDateFrom"
                />
              </template>
            </v-text-field>
          </v-col>
          <v-col cols="6" md="3">
            <!-- 配布場所 -->
            <v-select
              v-model="searchItem.facility"
              :items="facilities"
              label="配布場所"
              :placeholder="config.PLACEHOLDER_SELECT"
              return-object
              clearable
              outlined
            ></v-select>
          </v-col>
        </v-row>
        <v-divider class="mb-1" />
      </v-container>
      <v-container fluid style="padding: 0">
        <!-- 合計表示部 -->
        <p style="margin: 5px 0 15px 15px">合計</p>
        <v-data-table
          :headers="sumHeaders"
          :items="sumItems"
          locale="ja-jp"
          hide-default-footer
          disable-sort
        >
        </v-data-table>
        <!-- 一覧表示部 -->
        <p style="margin: 15px 0 15px 15px">一覧</p>
        <v-divider class="mb-1" />
        <top-operation-data-table
          :headers="tableHeaders"
          :items="lodineDistribution"
          locale="ja-jp"
          ref="dist"
          :sort-by="['dist_date']"
          :sort-desc="true"
          must-sort
          disp-mode="nowrap"
          class="fixedWidth"
        >
          <template v-slot:[`item.dist_date`]="{ item }">
            {{
              $dateUtils.formatDatetime(
                item.dist_date,
                config.DATE_FORMAT_YYYYMMDD_WITH_SLASH
              )
            }}
          </template>
          <template v-slot:[`item.fa_name`]="{ item }">
            {{ facilityName(item) }}
          </template>
        </top-operation-data-table>
      </v-container>
    </v-sheet>
    <v-overlay :value="overlay" opacity="1" color="white">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import Vue from "vue";
import DatePicker from "@/components/DatePicker";
import TopOperationDataTable from "@/components/ui/TopOperationDataTable";
import config from "@/js/const";
import DateUtils from "@/plugins/date-utils";
import Disaster from "@/plugins/disaster";
import messageUtils from "@/mixins/message-utils";
import { ScreenId } from "@/js/value-objects/screen-id";
Vue.use(DateUtils);
Vue.use(Disaster);

/**
 * 集計対象のキー名配列(APIのレスポンスと同じ名前を指定)
 */
const SUMMARY_TARGET_KEYS = [
  // 対象（受付）者数
  "target_count",
  // 配布者数
  "distribution_count",
  // 未配布者数
  "undistribution_count",
  // 配布せず（安定ヨウ素剤アレルギー等）
  "allergy_count",
  // 受取辞退
  "decline_count",
  // 丸剤2丸
  "lodine_tablets_count1",
  // 丸剤1丸
  "lodine_tablets_count2",
  // ゼリー剤32.5mg
  "lodine_tablets_count3",
  // ゼリー剤16.3mg
  "lodine_tablets_count4"
];

/**
 * 集計対象の各キーの値が初期値(0)に設定されたオブジェクトを作成する。
 */
const createInitialSumItem = (initialValue = 0) =>
  Object.fromEntries(SUMMARY_TARGET_KEYS.map(key => [key, initialValue]));

export default {
  mixins: [messageUtils],
  components: {
    DatePicker,
    TopOperationDataTable, // $refs で参照するため、動的インポートはしない
    ErrorMessage: () => import("@/components/ErrorMessage"),
    SuccessMessage: () => import("@/components/SuccessMessage")
  },
  data: () => ({
    screenTitle: ScreenId.getScreenTitle(ScreenId.SF1001),
    overlay: false,
    config: config,

    /**
     * 検索部の入力値
     */
    searchItem: {
      // 災害
      disaster: "",
      // 配布日（開始）
      selectedDistDateFrom: "",
      // 配布日（終了）
      selectedDistDateTo: "",
      // 配布場所
      facility: ""
    },

    /**
     * 一覧表示部のデータの集計結果
     */
    sumItems: [createInitialSumItem()]
  }),

  mounted() {
    this.pageTitle(this.screenTitle);
    // 一覧データテーブルの更新を待って集計する
    this.$nextTick(() => this.updateSumItems());
  },

  created() {
    this.initialize();
  },

  watch: {
    /**
     * ストア(安定ヨウ素剤配布状況)の変更を監視
     */
    lodineDistribution() {
      // 一覧データテーブルの更新を待って集計する
      this.$nextTick(() => this.updateSumItems());
    },

    /**
     * 検索部の各値の変更を監視
     */
    searchItem: {
      handler() {
        // 一覧データテーブルの更新を待って集計する
        this.$nextTick(() => this.updateSumItems());
      },
      deep: true
    },

    $route: "initialize"
  },

  computed: {
    disasters() {
      return this.$store.state.disasterInfo.data.results;
    },
    lodineDistribution() {
      if (!this.$store.state.lodineDistribution.data.results) return [];
      return this.$store.state.lodineDistribution.data.results[0].situations;
    },
    facilities() {
      if (!this.$store.state.lodineDistribution.data.results) return [];
      return this.$store.state.lodineDistribution.data.results[0]
        .distribution_place_list;
    },
    sumHeaders() {
      return [
        {
          text: "対象（受付）者数",
          value: "target_count"
        },
        {
          text: "配布者数",
          value: "distribution_count"
        },
        {
          text: "未配布者数",
          value: "undistribution_count"
        },
        {
          text: "配布せず（安定ヨウ素剤アレルギー等）",
          value: "allergy_count",
          class: "text-pre"
        },
        {
          text: "受取辞退",
          value: "decline_count"
        },
        {
          text: "丸剤2丸",
          value: "lodine_tablets_count1"
        },
        {
          text: "丸剤1丸",
          value: "lodine_tablets_count2"
        },
        {
          text: "ゼリー剤32.5mg",
          value: "lodine_tablets_count3"
        },
        {
          text: "ゼリー剤16.3mg",
          value: "lodine_tablets_count4"
        }
      ];
    },
    tableHeaders() {
      return [
        {
          text: "災害No",
          align: " d-none",
          value: "disaster_no",
          filter: this.disasterFilter
        },
        {
          text: "配布日",
          value: "dist_date",
          filter: this.distDateFilter
        },
        {
          text: "配布場所",
          value: "fa_name",
          filter: this.facilityFilter,
          sortable: false
        },
        {
          text: "対象（受付）者数",
          value: "target_count"
        },
        {
          text: "配布者数",
          value: "distribution_count"
        },
        {
          text: "未配布者数",
          value: "undistribution_count"
        },
        {
          text: "配布せず（安定ヨウ素剤アレルギー等）",
          value: "allergy_count",
          class: "text-pre"
        },
        {
          text: "受取辞退",
          value: "decline_count"
        },
        {
          text: "丸剤2丸",
          value: "lodine_tablets_count1"
        },
        {
          text: "丸剤1丸",
          value: "lodine_tablets_count2"
        },
        {
          text: "ゼリー剤32.5mg",
          value: "lodine_tablets_count3"
        },
        {
          text: "ゼリー剤16.3mg",
          value: "lodine_tablets_count4"
        }
      ];
    },
    distDateFrom: {
      get: function() {
        return this.$dateUtils.toHyphenFromSlash(
          this.searchItem.selectedDistDateFrom
        );
      },
      set: function() {}
    },

    distDateTo: {
      get: function() {
        return this.$dateUtils.toHyphenFromSlash(
          this.searchItem.selectedDistDateTo
        );
      },
      set: function() {}
    }
  },

  methods: {
    async initialize() {
      this.overlay = true;
      await Promise.all([
        this.$store.dispatch("disasterInfo/loadDisasterInfo", ScreenId.SF1001),
        this.$store.dispatch(
          "lodineDistribution/loadLodineDistribution",
          ScreenId.SF1001
        )
      ]).catch(e => {
        this.handleError(e, ScreenId.SF1001, "aggregation-menu");
      });
      this.searchItem.disaster = this.$disaster;
      this.overlay = false;
    },

    disasterFilter(value) {
      this.resetPage();

      if (!this.searchItem.disaster) {
        return true;
      }
      if (this.searchItem.disaster.disaster_no === "") {
        return true;
      }
      return value === this.searchItem.disaster.disaster_no;
    },

    distDateFilter(value) {
      this.resetPage();

      const paramDatetime = this.$moment(
        value,
        config.DATETIME_FORMAT_YYYYMMDDHHMMSS_WITH_SLASH_COLON
      ).format(config.DATE_FORMAT_YYYYMMDD);
      let datetimeFrom = null;
      if (this.distDateFrom) {
        datetimeFrom = this.formatDatetimeForSearch(this.distDateFrom);
      }
      let datetimeTo = null;
      if (this.distDateTo) {
        datetimeTo = this.formatDatetimeForSearch(this.distDateTo);
      }
      if (!paramDatetime) {
        return false;
      }
      if (datetimeFrom && datetimeTo) {
        return paramDatetime >= datetimeFrom && paramDatetime <= datetimeTo;
      }
      if (datetimeFrom) {
        return paramDatetime >= datetimeFrom;
      }
      if (datetimeTo) {
        return paramDatetime <= datetimeTo;
      }
      return true;
    },

    facilityFilter(value) {
      this.resetPage();

      if (!this.searchItem.facility || this.searchItem.facility === "") {
        return true;
      }
      return value === this.searchItem.facility;
    },

    /**
     * 現在一覧部に表示されているアイテムをもとに、集計結果を更新する。
     */
    updateSumItems() {
      // 一覧表示されている、フィルタ適用後データ
      const filteredItems =
        this.$refs.dist?.$children[0].$children[0].filteredItems ?? [];

      // フィルタ適用後データを集計
      this.sumItems = [
        filteredItems.reduce(
          (prev, current) =>
            Object.fromEntries(
              SUMMARY_TARGET_KEYS.map(key => [
                key,
                prev[key] + Number(current[key])
              ])
            ),
          createInitialSumItem()
        )
      ];
    },

    facilityName(item) {
      if (item.fa_name === null || item.fa_name === "") {
        return "臨時配布場所等その他の配布場所";
      }
      return item.fa_name;
    },

    formatDatetimeForSearch(datetime) {
      const formattedDatetime = this.$moment(new Date(datetime));
      if (formattedDatetime.isValid()) {
        return formattedDatetime.format(config.DATE_FORMAT_YYYYMMDD);
      }
      return null;
    },

    /**検索時にページネーションをリセット
     * 初期表示ではテーブルが生成されていないためデータテーブルのメソッドが
     * 呼び出せない。生成されていることを確認してから呼びだす。
     */
    resetPage() {
      if (this.$refs.dist) {
        this.$refs.dist.resetPage();
      }
    }
  }
};
</script>
